// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { withCustomersAccess } from "@crm-src/components/page-permission-guard"

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom";
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import type { ChipProps } from "@mui/material"
import { PageHeader } from "@/components/ui/page-header"
import { PageLoader } from "@/components/ui/page-loader"
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination"

import {
  UserPlus,
  Search,
  X,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  Target,
  AlertCircle,
  Clock,
  Building2,
  Users,
} from "lucide-react"

import { StatsCard, ColoredDot, DollarIcon } from "@crm-src/components/stats-card"
import { ImportExportButtons } from "@crm-src/components/import-export-buttons"
import { CustomerFormModal } from "@crm-src/components/customer-form-modal"
import { DeleteCustomerModal } from "@crm-src/components/delete-customer-modal"
import { customersApi } from "@crm-src/lib/customers-api"
import { formatIndustry, INDUSTRY_OPTIONS, PROSPECT_INDUSTRIES } from "@crm-src/lib/industries"
import { useResellers } from "@crm-src/hooks/useResellers"
import { useUsers } from "@crm-src/hooks/useUsers"
import { useTableQuery } from "@crm-src/components/table/useTableQuery"
import { useDebouncedSearch } from "@crm-src/components/table/useDebouncedSearch"
import { useLatestRequest } from "@crm-src/components/table/useLatestRequest"
import { useTableSelection } from "@crm-src/components/table/useTableSelection"
import { buildTableParams } from "@crm-src/components/table/table-query"
import { SortableHeader } from "@crm-src/components/table/SortableHeader"
import { TableFilterBar } from "@crm-src/components/table/TableFilterBar"
import { TableEmptyState } from "@crm-src/components/table/TableEmptyState"
import { CUSTOMERS_TABLE } from "@crm-src/components/table/configs"
import type { TableColumn } from "@crm-src/components/table/configs"
import { buildCustomerFacetDefs, nextRenewalDate } from "./customers-page-query"
import type { CustomerFacetKey } from "./customers-page-query"
import { InlineSalespeopleEditor, InlineSelectEditor } from "@crm-src/components/inline-edit"
import type {
  Customer,
  DetailedCustomer,
  CustomerCreateParams,
  CustomerUpdateParams,
  CustomerFilters,
  CustomerStats,
  CustomerSupportLevel,
  CustomerBusinessCategory
} from "@crm-src/types/customer"
// Financial wall: this list shows NO per-customer money (no MRR / value /
// revenue column or stat card), so there is nothing to redact here and the
// `formatCurrency` import was dead. Removed so the safety-net test stays
// green; if a money column is ever added, render it through
// `<Money>` from "@crm-src/components/money" instead of formatCurrency.
import { BulkDeleteCustomerModal } from "@crm-src/components/bulk-delete-customer-modal"
import { BulkAssignSalespeopleModal } from "@crm-src/components/bulk-assign-salespeople-modal"
import { BulkAssignResellerModal } from "@crm-src/components/bulk-assign-reseller-modal"
import { BulkActionsBar } from "@/components/ui/bulk-actions-bar"
import usePermissions from "@crm-src/hooks/usePermissions"
import { useResellerAccess } from "@crm-src/components/reseller"

/** Rows per page. Must match the `per_page` the request sends and the value
 * the pagination footer falls back to. */
const PER_PAGE = 25

function CustomersPage() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const { isAdmin } = usePermissions()
  // Reseller wall: hide the "Reseller" column + its inline edit + the
  // "Assign Reseller" bulk action from viewers without crm:resellers.
  // See components/reseller.tsx.
  const canViewResellers = useResellerAccess()
  const { resellers } = useResellers()
  // Reseller options for the inline-edit select. "none" === Direct (no
  // reseller) — the same sentinel the bulk-assign-reseller modal sends. Goes
  // through bulkAssignReseller (NOT customer update, which strong-params drops
  // reseller_id — reseller is agreement-scoped on the customer record).
  const resellerOptions = [
    { value: "none", label: "Direct" },
    ...resellers.map((r) => ({ value: String(r.id), label: r.name })),
  ]
  
  // State management
  const [customers, setCustomers] = useState<Customer[]>([])
  const [stats, setStats] = useState<CustomerStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [isCustomerFormOpen, setIsCustomerFormOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null)
  const [customerToEdit, setCustomerToEdit] = useState<DetailedCustomer | null>(null)
  
  // Bulk selection state. The selected ROWS live in `useTableSelection` below,
  // which clears them whenever the result set changes.
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false)
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false)
  const [isBulkAssignResellerModalOpen, setIsBulkAssignResellerModalOpen] = useState(false)

  // Per-row actions menu anchor (keyed by customer id) — replaces the shadcn
  // DropdownMenu during the MUI 5 migration.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null)

  const searchInputRef = useRef<HTMLInputElement>(null)

  // Sort / facet / page state. The URL is the SINGLE source of truth — there is
  // deliberately no `useState` copy of any of it, so a refresh, the back button
  // and a pasted link all reproduce the same view. See components/table/.
  //
  // Reseller wall: a viewer without crm:resellers never gets the `reseller_id`
  // facet, and dropping it from `facetKeys` (not just from the rendered bar)
  // means a hand-typed `?reseller_id=…` is ignored for them too rather than
  // silently narrowing a list whose Reseller column they cannot see.
  //
  // Annotated, not inferred: TypeScript reads `(k) => k !== "reseller_id"` as a
  // type predicate and would otherwise give the two branches DIFFERENT key
  // unions, which then disagree with the facet defs built from the full set.
  const facetKeys = useMemo<readonly CustomerFacetKey[]>(
    () => (canViewResellers
      ? CUSTOMERS_TABLE.facetKeys
      : CUSTOMERS_TABLE.facetKeys.filter((k) => k !== "reseller_id")),
    [canViewResellers],
  )
  const table = useTableQuery({
    defaultSort: CUSTOMERS_TABLE.defaultSort,
    defaultDir: CUSTOMERS_TABLE.defaultDir,
    facetKeys,
  })

  // Only the TEXT box debounces (500ms) — a header or facet click fires at
  // once. The hook also owns the "a new term means page 1 again" reset, which
  // is the part that loops if a page writes it inline.
  const search = useDebouncedSearch(table.setPage)

  const { users } = useUsers()
  const facetDefs = useMemo(
    () => buildCustomerFacetDefs(resellers, users, PROSPECT_INDUSTRIES)
      .filter((def) => facetKeys.includes(def.key)),
    [resellers, users, facetKeys],
  )
  const columns = useMemo<readonly TableColumn[]>(
    () => CUSTOMERS_TABLE.columns.filter((col) => canViewResellers || col.key !== "reseller"),
    [canViewResellers],
  )

  // Pagination state (what the LAST response reported — the requested page
  // lives in the URL via `table.page`).
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: PER_PAGE
  })

  // The exact params the list request sends. Derived, never stored: sort/dir/
  // facets/page come from `table` (i.e. the URL) and the search term from the
  // debounce above. Also handed to the export button so a CSV matches the
  // filtered view on screen.
  //
  // `buildTableParams` is generic over the page's facet-key union, so the
  // params it returns type-check against `CustomerFilters` field by field: a
  // facet key the API does not implement is a compile error here, not a param
  // the server silently drops. No cast.
  const listParams = useMemo<CustomerFilters>(() => ({
    ...buildTableParams(
      { sort: table.sort, dir: table.dir },
      table.facets,
      table.page,
      PER_PAGE,
      search.search,
    ),
    // Only actual customers, not prospects/leads.
    customer_type: 'Customer',
    // `include_deals` is left at its server default (ON): the "Next Renewal"
    // column is computed in the browser from `customer.customer_deals`, so
    // opting out would blank that column for every row.
  }), [table.sort, table.dir, table.facets, table.page, search.search])

  // Bulk selection, cleared whenever `listParams` describes a different result
  // set — otherwise "Delete 3" stays armed against rows a new filter has taken
  // off screen. See useTableSelection.
  const {
    selected: selectedCustomers,
    setSelected: setSelectedCustomers,
    clear: clearSelection,
  } = useTableSelection<Customer>(listParams)

  // Drops a slow earlier response that would otherwise land after a faster
  // later one. See useLatestRequest.
  const beginRequest = useLatestRequest()

  // Load customers
  const loadCustomers = useCallback(async () => {
    const isCurrent = beginRequest()
    try {
      setLoading(true)
      const response = await customersApi.getAll(listParams)
      if (!isCurrent()) return
      setCustomers(response.customers || [])
      
      setPagination({
        current_page: response.pagination?.current_page || 1,
        total_pages: response.pagination?.total_count ? Math.ceil(response.pagination.total_count / (response.pagination.per_page || PER_PAGE)) : 1,
        total_count: response.pagination?.total_count || 0,
        per_page: response.pagination?.per_page || PER_PAGE
      })
      if (response.stats) {
        setStats(response.stats)
      }
    } catch (error: any) {
      if (!isCurrent()) return
      console.error('❌ Error loading customers:', error)
      toast({
        title: "Error",
        description: "Failed to load customers",
        variant: "destructive",
      })
      // Set empty data on error
      setCustomers([])
      setPagination({
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: PER_PAGE
      })
    } finally {
      if (isCurrent()) setLoading(false)
    }
  }, [listParams, beginRequest, toast])

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const statsResponse = await customersApi.getStats()
      setStats(statsResponse)
    } catch (error) {
      console.error('Error loading customer stats:', error)
    }
  }, [])

  // Initial load
  useEffect(() => {
    loadCustomers()
    loadStats()
  }, [loadCustomers, loadStats])

  // Clear search input
  const handleClearSearch = () => {
    search.clear()
    searchInputRef.current?.focus()
  }

  // Handle customer form success (both create and update)
  const handleCustomerFormSuccess = (customer: DetailedCustomer) => {
    // Update the customer in the local list if it exists
    setCustomers(prev => {
      const existingIndex = prev.findIndex(c => c.id === customer.id)
      if (existingIndex >= 0) {
        // Update existing customer
        const updated = [...prev]
        updated[existingIndex] = { ...updated[existingIndex], ...customer }
        return updated
      }
      // New customer — splice it in at the top of the local state so
      // the row is visible immediately, before the loadCustomers
      // refresh below settles. The reload will replace this with the
      // canonical server payload if the row drifts in sort order.
      return [customer as unknown as Customer, ...prev]
    })
    
    // Clear edit state
    setCustomerToEdit(null)
    
    // Refresh data
    loadCustomers()
    loadStats()
  }

  // Handle view customer
  const handleViewCustomer = (customer: Customer) => {
    navigate(`/crm/customers/${customer.id}`)
  }

  // Handle edit customer
  const handleEditCustomer = async (customer: Customer) => {
    try {
      const response = await customersApi.getById(customer.id)
      const fullCustomer = response.customer
      
      setCustomerToEdit(fullCustomer)
      setIsCustomerFormOpen(true)
    } catch (error) {
      console.error('Error loading customer for editing:', error)
      toast({
        title: "Error",
        description: "Failed to load customer details for editing",
        variant: "destructive",
      })
    }
  }

  // Handle delete customer
  const handleDeleteCustomer = (customer: Customer) => {
    setCustomerToDelete(customer)
    setIsDeleteDialogOpen(true)
  }

  // Handle bulk delete
  const handleBulkDelete = async (customerIds: string[]) => {
    try {
      const result = await customersApi.bulkDelete(customerIds)
      
      if (result.failed_deletions && result.failed_deletions.length > 0) {
        // Some deletions failed
        toast({
          title: "Partial Success",
          description: `${result.deleted_count} customers deleted successfully. ${result.failed_deletions.length} customers could not be deleted due to dependencies.`,
          variant: "default",
        })
      } else {
        // All deletions successful
        toast({
          title: "Success",
          description: `${result.deleted_count} customer${result.deleted_count > 1 ? 's' : ''} deleted successfully`,
        })
      }
      
      // Clear selection and reload customers
      clearSelection()
      loadCustomers()
    } catch (error: any) {
      console.error('Error bulk deleting customers:', error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete customers",
        variant: "destructive",
      })
    }
  }

  // Handle bulk assign salespeople
  const handleBulkAssignSalespeople = async (customerIds: string[], salespeopleIds: string[]) => {
    try {
      const result = await customersApi.bulkAssignSalespeople(customerIds, salespeopleIds)
      
      if (result.failed_assignments && result.failed_assignments.length > 0) {
        // Some assignments failed
        toast({
          title: "Partial Success",
          description: `Salespeople assigned to ${result.assigned_count} customers successfully. ${result.failed_assignments.length} customers could not be updated.`,
          variant: "default",
        })
      } else {
        // All assignments successful
        toast({
          title: "Success",
          description: `Salespeople assigned to ${result.assigned_count} customer${result.assigned_count > 1 ? 's' : ''} successfully (${result.total_assignments} total assignments)`,
        })
      }
      
      // Clear selection and reload customers
      clearSelection()
      loadCustomers()
    } catch (error: any) {
      console.error('Error bulk assigning salespeople:', error)
      toast({
        title: "Error",
        description: error.response?.data?.message || error.response?.data?.errors?.[0] || "Failed to assign salespeople",
        variant: "destructive",
      })
    }
  }

  // Handle bulk assign reseller
  const handleBulkAssignReseller = async (customerIds: string[], resellerId: string) => {
    try {
      const result = await customersApi.bulkAssignReseller(customerIds, resellerId)
      
      if (result.failed_assignments && result.failed_assignments.length > 0) {
        // Some assignments failed
        toast({
          title: "Partial Success",
          description: `Reseller assigned to ${result.assigned_count} customers successfully. ${result.failed_assignments.length} customers could not be updated.`,
          variant: "default",
        })
      } else {
        // All assignments successful
        toast({
          title: "Success",
          description: `Reseller assigned to ${result.assigned_count} customer${result.assigned_count > 1 ? 's' : ''} successfully`,
        })
      }
      
      // Clear selection and reload customers
      clearSelection()
      loadCustomers()
    } catch (error: any) {
      console.error('Error bulk assigning reseller:', error)
      toast({
        title: "Error",
        description: error.response?.data?.message || error.response?.data?.errors?.[0] || "Failed to assign reseller",
        variant: "destructive",
      })
    }
  }

  // Handle customer deletion from modal
  const handleCustomerDeleted = (deletedCustomerId: string) => {
    // Remove the customer from the local list
    setCustomers(prev => prev.filter(customer => customer.id !== deletedCustomerId))
    
    // Close the delete modal
    setCustomerToDelete(null)
    
    // Refresh data
    loadCustomers()
    loadStats()
  }

  // Get status color
  // Handle pagination — writes straight to the URL, like sort and the facets.
  const handlePageChange = (page: number) => {
    table.setPage(page)
  }

  // Format support level for display
  const formatSupportLevel = (supportLevel: CustomerSupportLevel) => {
    switch (supportLevel) {
      case 'none':
        return 'NONE'
      case 'weekdays':
        return 'WEEK DAYS'
      case '24_7':
        return '24/7'
      default:
        return 'NONE'
    }
  }

  // Format business category for display
  const formatBusinessCategory = (businessCategory: CustomerBusinessCategory) => {
    switch (businessCategory) {
      case 'service':
        return 'SERVICE'
      case 'equipment_purchaser':
        return 'EQUIPMENT'
      default:
        return 'EQUIPMENT'
    }
  }

  // Get support level Chip color (inlined MUI palette during the MUI 5
  // migration — was a Tailwind bg/text class pair).
  const getSupportLevelColor = (supportLevel: CustomerSupportLevel): ChipProps['color'] => {
    switch (supportLevel) {
      case 'none':
        return 'default'
      case 'weekdays':
        return 'info'
      case '24_7':
        return 'success'
      default:
        return 'default'
    }
  }

  // Get business category Chip color (inlined MUI palette during the MUI 5
  // migration — was a Tailwind bg/text class pair).
  const getBusinessCategoryColor = (businessCategory: CustomerBusinessCategory): ChipProps['color'] => {
    switch (businessCategory) {
      case 'service':
        return 'secondary'
      case 'equipment_purchaser':
        return 'warning'
      default:
        return 'warning'
    }
  }

  // Option lists for the inline-edit selects (labels reuse the same formatters
  // as the read display so the picker and the chip read identically).
  const SUPPORT_LEVEL_OPTIONS = (['none', 'weekdays', '24_7'] as CustomerSupportLevel[]).map((v) => ({
    value: v,
    label: formatSupportLevel(v),
  }))
  const BUSINESS_CATEGORY_OPTIONS = (['service', 'equipment_purchaser'] as CustomerBusinessCategory[]).map((v) => ({
    value: v,
    label: formatBusinessCategory(v),
  }))

  // Inline edit (list rows): assign salesperson / change support level /
  // business type right from the table without opening the edit modal. Both
  // throw on failure so the shared <InlineEditField> surfaces the error toast;
  // on success we reload so the canonical row catches up. Gated on isAdmin() to
  // match the bulk actions. See docs/crm.md → "Inline editing".
  const handleInlineAssignSalespeople = async (customerId: string, userIds: string[]) => {
    await customersApi.bulkAssignSalespeople([customerId], userIds)
    await loadCustomers()
    toast({ title: "Saved", description: "Salesperson updated" })
  }

  const handleInlineUpdateCustomer = async (customerId: string, patch: CustomerUpdateParams) => {
    await customersApi.update(customerId, patch)
    await loadCustomers()
    toast({ title: "Saved", description: "Customer updated" })
  }

  // Reseller assignment reuses the bulk endpoint (bulk-of-one); "none" === Direct.
  const handleInlineAssignReseller = async (customerId: string, resellerId: string) => {
    await customersApi.bulkAssignReseller([customerId], resellerId)
    await loadCustomers()
    toast({ title: "Saved", description: "Reseller updated" })
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header */}
      <PageHeader
        title="Customers"
        description="Manage your customer relationships and drive success"
        actions={
          <>
            {/* Port divergence: the command center's "World Map" button stood
                here, navigating to `/crm/customers/world-map`. That page was
                never ported (see src/crm/README.md → "What is ported, and what
                is not"), so in this portal the click fell through
                `<CrmPrefixRedirect>` and App.tsx's catch-all onto the
                overview — a dead end that looked like a working feature.
                Removed rather than left dangling. If the map is ever ported,
                restore the button here and add the route in App.tsx. */}
            <ImportExportButtons
              entityType="customer"
              entityDisplayName="Customers"
              apiService={customersApi}
              onImportComplete={loadCustomers}
              variant="buttons"
              size="default"
              filters={listParams}
            />
            <Button
              variant="contained"
              startIcon={<UserPlus size={16} />}
              onClick={() => {
                setCustomerToEdit(null)
                setIsCustomerFormOpen(true)
              }}
            >
              Add Customer
            </Button>
          </>
        }
      />

      {/* Customer Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 2 }}>
        <StatsCard
          title="Total Customers"
          value={pagination.total_count}
          icon={Users}
          iconColor="text-blue-600"
        />
        <StatsCard
          title="Renewals Close to Due"
          value={stats?.contracts_expiring_soon || 0}
          valueColor="text-yellow-600"
          icon={Clock}
          iconColor="text-yellow-600"
        />
        <StatsCard
          title="Renewals Due"
          value={stats?.renewals_due_soon || 0}
          valueColor="text-red-600"
          icon={RefreshCw}
          iconColor="text-red-600"
        />
        {/* Replaced the previous "Invoices Close to Due" / "Invoices Due"
            cards that were hard-coded to 0 — the stats endpoint never
            shipped those counts and they always rendered as "0", which
            looked like real (broken) data. Swapped for VIP + At-Risk
            counters that already exist on `CustomerStats`. If invoice
            ageing buckets are added to the API later, add them back
            and verify they render before merging. */}
        <StatsCard
          title="VIP Customers"
          value={stats?.vip_customers || 0}
          valueColor="text-purple-600"
          icon={Target}
          iconColor="text-purple-600"
        />
        <StatsCard
          title="At Risk"
          value={stats?.at_risk_customers || 0}
          valueColor="text-red-600"
          icon={AlertCircle}
          iconColor="text-red-600"
        />
      </Box>

      {/* Customers Table */}
      <Card>
        <CardHeader
          // The header now hosts a whole filter row, not one field: render it
          // as a <div> so the Boxes/Selects inside are not nested in a <span>.
          titleTypographyProps={{ component: "div" }}
          title={
            // The search box is WRAPPED, not replaced — same field, same 500ms
            // debounce, same clear button; the facets simply sit beside it.
            <TableFilterBar
              facetDefs={facetDefs}
              values={table.facets}
              onChange={table.setFacet}
              onClearAll={table.clearFacets}
              searchSlot={
                <TextField
                  inputRef={searchInputRef}
                  placeholder="Search customers by name, email, or company..."
                  value={search.input}
                  onChange={(e) => search.setInput(e.target.value)}
                  size="small"
                  sx={{ width: { xs: "100%", sm: 340 } }}
                  autoComplete="off"
                  name="search-customers-query"
                  id="search-customers-query"
                  type="search"
                  inputProps={{ role: "search", "data-form-type": "other" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ display: "flex", color: search.isSearching ? "primary.main" : "text.disabled" }}><Search size={16} /></Box>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {search.input && !search.isSearching && (
                          <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                            <X size={16} />
                          </IconButton>
                        )}
                        {search.isSearching && (
                          <Typography component="span" sx={{ fontSize: "0.875rem", color: "primary.main" }}>
                            Searching...
                          </Typography>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              }
            />
          }
        />
        <CardContent>
          {/* Bulk Actions Bar */}
          <BulkActionsBar
            selectedCount={selectedCustomers.length}
            entityNoun="customer"
            onClear={clearSelection}
          >
            {isAdmin() && (
              <>
                <Button
                  variant="contained"
                  onClick={() => setIsBulkAssignModalOpen(true)}
                  size="small"
                  startIcon={<Users size={16} />}
                >
                  Assign Salespeople
                </Button>
                {canViewResellers && (
                  <Button
                    variant="contained"
                    onClick={() => setIsBulkAssignResellerModalOpen(true)}
                    size="small"
                    startIcon={<Building2 size={16} />}
                  >
                    Assign Reseller
                  </Button>
                )}
              </>
            )}
            <Button
              variant="contained"
              color="error"
              onClick={() => setIsBulkDeleteModalOpen(true)}
              size="small"
              startIcon={<Trash2 size={16} />}
            >
              Delete {selectedCustomers.length}
            </Button>
          </BulkActionsBar>

          {loading ? (
            <PageLoader />
          ) : customers.length === 0 ? (
            // Names whichever narrowing actually emptied the list: filtering
            // to nothing used to claim the system held no customers at all.
            <TableEmptyState
              entityNoun="customers"
              hasSearch={search.input.trim().length > 0}
              activeFilterCount={table.activeFilterCount}
              onClearFilters={table.clearFacets}
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}>
                      <Checkbox
                        checked={selectedCustomers.length === customers.length && customers.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCustomers(customers)
                          } else {
                            clearSelection()
                          }
                        }}
                      />
                    </TableCell>
                    {/* The eight DATA columns, in `CUSTOMERS_TABLE` order —
                        which is the order the body cells below are written in,
                        so the two must be edited together. Reseller wall: the
                        "Reseller" column is filtered out of `columns` for
                        viewers without crm:resellers (its body cell is
                        conditional in lockstep). The checkbox and actions cells
                        either side stay plain <TableCell>s: neither maps to a
                        server column, so neither can sort. */}
                    {columns.map((col) => (
                      <SortableHeader
                        key={col.key}
                        column={col.key}
                        label={col.label}
                        sort={table.sort}
                        dir={table.dir}
                        onSort={table.toggleSort}
                        {...(col.align ? { align: col.align } : {})}
                        {...(col.key === "code" ? { sx: { whiteSpace: "nowrap" } } : {})}
                      />
                    ))}
                    <TableCell sx={{ width: 50 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow
                      key={customer.id}
                      hover
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedCustomers.some(c => c.id === customer.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCustomers([...selectedCustomers, customer])
                            } else {
                              setSelectedCustomers(selectedCustomers.filter(c => c.id !== customer.id))
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
                        onClick={() => handleViewCustomer(customer)}
                      >
                        <Box component="span" sx={{ display: "inline-block", whiteSpace: "nowrap", fontSize: "0.75rem", fontWeight: 600, color: "text.secondary", bgcolor: "action.hover", px: 0.75, py: 0.25, borderRadius: 1 }}>
                          {customer.customer_id || customer.id}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleViewCustomer(customer)}
                      >
                        <Typography sx={{ fontWeight: 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>{customer.company_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <InlineSelectEditor
                          canEdit={isAdmin()}
                          value={customer.business_category || 'equipment_purchaser'}
                          options={BUSINESS_CATEGORY_OPTIONS}
                          onSave={(v) => handleInlineUpdateCustomer(String(customer.id), { business_category: v as CustomerBusinessCategory })}
                          ariaLabel="Edit business type"
                          errorMessage="Failed to update business type"
                        >
                          <Chip size="small" color={getBusinessCategoryColor(customer.business_category || 'equipment_purchaser') ?? "default"} label={formatBusinessCategory(customer.business_category || 'equipment_purchaser')} />
                        </InlineSelectEditor>
                      </TableCell>
                      {/* Reseller wall: drop the "Reseller" cell (must stay in
                          lockstep with the header above). */}
                      {canViewResellers && (
                        <TableCell>
                          <InlineSelectEditor
                            canEdit={isAdmin()}
                            value={(customer as any).reseller ? String((customer as any).reseller.id) : "none"}
                            options={resellerOptions}
                            onSave={(v) => handleInlineAssignReseller(String(customer.id), v)}
                            ariaLabel="Edit reseller"
                            errorMessage="Failed to update reseller"
                          >
                            {(customer as any).reseller ? (
                              <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}>{(customer as any).reseller.name}</Typography>
                            ) : (
                              <Typography component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>Direct</Typography>
                            )}
                          </InlineSelectEditor>
                        </TableCell>
                      )}
                      <TableCell>
                        <InlineSelectEditor
                          canEdit={isAdmin()}
                          value={customer.support_level || 'none'}
                          options={SUPPORT_LEVEL_OPTIONS}
                          onSave={(v) => handleInlineUpdateCustomer(String(customer.id), { support_level: v as CustomerSupportLevel })}
                          ariaLabel="Edit support level"
                          errorMessage="Failed to update support level"
                        >
                          <Chip size="small" color={getSupportLevelColor(customer.support_level || 'none') ?? "default"} label={formatSupportLevel(customer.support_level || 'none')} />
                        </InlineSelectEditor>
                      </TableCell>
                      <TableCell>
                        <InlineSalespeopleEditor
                          canEdit={isAdmin()}
                          value={(customer.assigned_salespeople ?? []).map((s) => s.user_id)}
                          onSave={(ids) => handleInlineAssignSalespeople(String(customer.id), ids)}
                        >
                          {customer.assigned_salespeople && customer.assigned_salespeople.length > 0 ? (
                            <Box>
                              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}>
                                {customer.salespeople_names || customer.assigned_salespeople[0].full_name}
                              </Typography>
                              {customer.assigned_salespeople.length > 1 && (
                                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                  +{customer.assigned_salespeople.length - 1} more
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>Unassigned</Typography>
                          )}
                        </InlineSalespeopleEditor>
                      </TableCell>
                      <TableCell>
                        <InlineSelectEditor
                          canEdit={isAdmin()}
                          value={customer.industry ?? ""}
                          options={INDUSTRY_OPTIONS}
                          onSave={(v) => handleInlineUpdateCustomer(String(customer.id), { industry: v })}
                          ariaLabel="Edit industry"
                          errorMessage="Failed to update industry"
                        >
                          {customer.industry ? (
                            <Chip
                              label={formatIndustry(customer.industry)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: "0.75rem" }}
                            />
                          ) : (
                            <Typography component="span" sx={{ color: "text.disabled" }}>—</Typography>
                          )}
                        </InlineSelectEditor>
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleViewCustomer(customer)}
                      >
                        <Typography component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                          {/* `nextRenewalDate` is the extracted, unit-tested
                              version of the inline helper that used to live in
                              this file. It mirrors the server's
                              `renewal_date >= CURRENT_DATE` sort expression, so
                              a renewal due TODAY now shows here as well as
                              sorting first — see customers-page-query.ts. The
                              FORMATTING below is unchanged. */}
                          {nextRenewalDate(customer) ? new Date(nextRenewalDate(customer)!).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                          }) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          sx={{ width: 32, height: 32 }}
                          aria-label="Open menu"
                          onClick={(e) => {
                            e.stopPropagation()
                            setMenuAnchor({ el: e.currentTarget, id: customer.id })
                          }}
                        >
                          <MoreHorizontal size={16} />
                        </IconButton>
                        <Menu
                          anchorEl={menuAnchor?.id === customer.id ? menuAnchor.el : null}
                          open={menuAnchor?.id === customer.id}
                          onClose={() => setMenuAnchor(null)}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                          <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Actions</ListSubheader>
                          <MenuItem onClick={() => {
                            setMenuAnchor(null)
                            handleViewCustomer(customer)
                          }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
                            View Details
                          </MenuItem>
                          <MenuItem onClick={() => {
                            setMenuAnchor(null)
                            handleEditCustomer(customer)
                          }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                            Edit Customer
                          </MenuItem>
                          <Divider />
                          <MenuItem
                            onClick={() => {
                              setMenuAnchor(null)
                              handleDeleteCustomer(customer)
                            }}
                            sx={{ color: "error.main" }}
                          >
                            <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                            Delete Customer
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <Pagination
            {...paginationPropsFromMeta(pagination)}
            entityNoun="customers"
            hideIfSinglePage
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>





      {/* Customer Form Modal (Create/Edit) */}
      <CustomerFormModal
        isOpen={isCustomerFormOpen}
        onClose={() => {
          setIsCustomerFormOpen(false)
          setCustomerToEdit(null)
        }}
        customer={customerToEdit}
        onSuccess={handleCustomerFormSuccess}
      />

      {/* Delete Customer Modal */}
      <DeleteCustomerModal
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setCustomerToDelete(null)
        }}
        customer={customerToDelete}
        onCustomerDeleted={handleCustomerDeleted}
      />

      {/* Bulk Delete Modal */}
      <BulkDeleteCustomerModal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        selectedCustomers={selectedCustomers}
        onConfirmDelete={handleBulkDelete}
      />

      <BulkAssignSalespeopleModal
        isOpen={isBulkAssignModalOpen}
        onClose={() => setIsBulkAssignModalOpen(false)}
        selectedResellers={selectedCustomers.map(c => ({ id: c.id, name: c.company_name || c.full_name || `Customer ${c.id}`, ...(c.customer_id ? { code: c.customer_id } : {}) }))}
        onConfirmAssign={handleBulkAssignSalespeople}
      />

      {/* Reseller wall: never mount the bulk-assign-reseller modal for a viewer
          without crm:resellers (its trigger is already hidden). */}
      {canViewResellers && (
        <BulkAssignResellerModal
          isOpen={isBulkAssignResellerModalOpen}
          onClose={() => setIsBulkAssignResellerModalOpen(false)}
          selectedEntities={selectedCustomers.map(c => ({ id: c.id, company: c.full_name, ...(c.company_name ? { name: c.company_name } : {}) }))}
          entityType="customer"
          onConfirmAssign={handleBulkAssignReseller}
        />
      )}

    </Box>
  )
}

export default withCustomersAccess(CustomersPage)